import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import {
  LocationTypeField,
  SystemLocation,
  SbcApiService,
} from '../services/sbc-api.service';
import { SessionService } from '../../../core/services/session.service';
import { PageBackButtonComponent } from '../../../shared/components/page-back-button/page-back-button.component';
import { PageRefreshButtonComponent } from '../../../shared/components/page-refresh-button/page-refresh-button.component';
import { LocationLidarPanelComponent } from '../lidar-survey/location-lidar-panel.component';

interface LocTreeNode {
  location: SystemLocation;
  children: LocTreeNode[];
}

@Component({
  selector: 'app-areas',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, LocationLidarPanelComponent, PageRefreshButtonComponent, PageBackButtonComponent],
  templateUrl: './areas.component.html',
  styleUrl: './areas.component.css',
})
export class AreasComponent {
  private readonly api = inject(SbcApiService);
  readonly session = inject(SessionService);

  readonly loading = signal(false);
  readonly detailLoading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly toast = signal('');

  readonly locations = signal<SystemLocation[]>([]);
  readonly detail = signal<SystemLocation | null>(null);
  readonly selectedId = signal<number | null>(null);
  readonly expandedIds = signal<Set<number>>(new Set());
  readonly treeFilter = signal('');
  readonly formModalOpen = signal(false);
  readonly lidarModalOpen = signal(false);
  readonly lidarModalLocationId = signal<number | null>(null);

  readonly addModalOpen = signal(false);
  readonly addTypePicked = signal(false);
  readonly addFormErrors = signal<{
    form?: string;
    name?: string;
    code?: string;
    attributes?: Record<string, string>;
  }>({});

  addForm = {
    locationTypeId: null as number | null,
    parentLocationId: null as number | null,
    code: '',
    name: '',
    attributes: {} as Record<string, string>,
  };

  editForm = {
    name: '',
    code: '',
    attributes: {} as Record<string, string>,
  };

  readonly locationTypeCatalog = signal<
    { id: number; code: string; name: string; fields: LocationTypeField[] }[]
  >([]);

  readonly locationTree = computed(() => this.buildTree(this.locations()));

  readonly visibleTree = computed(() => {
    const q = this.treeFilter().trim().toLowerCase();
    if (!q) return this.locationTree();
    return this.filterTree(this.locationTree(), q);
  });

  readonly selectionPath = computed(() => {
    const id = this.selectedId();
    if (id == null) return [];
    const byId = new Map(this.locations().map((l) => [l.id, l]));
    const trail: SystemLocation[] = [];
    let cur = byId.get(id);
    while (cur) {
      trail.unshift(cur);
      cur = cur.parentLocationId != null ? byId.get(cur.parentLocationId) : undefined;
    }
    return trail;
  });

  readonly selectedTypeForAdd = computed(() => {
    const id = this.addForm.locationTypeId;
    if (!id) return null;
    return this.locationTypeCatalog().find((t) => t.id === id) ?? null;
  });

  readonly canManageLocations = computed(() =>
    ['officer', 'config_admin'].includes(this.session.role())
  );

  readonly canAccessPlotBoundaries = computed(() => this.session.canAccessConfig());

  readonly isSectorScanLevel = computed(() => {
    const d = this.detail();
    return d?.locationTypeCode === 'SECTOR';
  });

  readonly isScanEligibleType = computed(() => {
    const code = this.selectedTypeCode();
    return ['AUTHORITY', 'ZONE', 'SECTOR', 'PLOT'].includes(code);
  });

  readonly selectedTypeCode = computed(() => (this.detail()?.locationTypeCode || '').toUpperCase());

  readonly lidarModalLocation = computed(() => {
    const id = this.lidarModalLocationId();
    if (id == null) return null;
    return this.locations().find((l) => l.id === id) ?? null;
  });

  constructor() {
    effect(() => {
      this.session.authorityId();
      untracked(() => this.reloadTypesAndLocations());
    });
  }

  reloadTypesAndLocations(): void {
    this.loading.set(true);
    this.error.set('');
    this.api.listLocationTypes({ includeInactive: 'true' }).subscribe({
      next: (typeRes) => {
        this.locationTypeCatalog.set(
          (typeRes.data || []).map((t) => ({
            id: t.id,
            code: t.code,
            name: t.name,
            fields: t.fields || [],
          }))
        );
        this.loadLocations();
      },
      error: () => {
        this.locationTypeCatalog.set([]);
        this.loadLocations();
      },
    });
  }

  reload(): void {
    this.reloadTypesAndLocations();
  }

  private loadLocations(): void {
    this.api.listSystemLocations({ includeInactive: 'true' }).subscribe({
      next: (res) => {
        const rows = (res.data || []).map((loc) => {
          const type = this.locationTypeCatalog().find((t) => t.id === loc.locationTypeId);
          return { ...loc, fields: type?.fields || loc.fields || [] };
        });
        this.locations.set(rows);
        this.loading.set(false);
        this.initTreeExpansion();

        const selected = this.selectedId();
        if (selected != null && rows.some((l) => l.id === selected)) {
          this.expandAncestors(selected);
          this.loadDetail(selected);
        } else if (selected != null) {
          this.selectedId.set(null);
          this.detail.set(null);
          this.formModalOpen.set(false);
        }
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message || 'Failed to load locations');
      },
    });
  }

  loadDetail(id: number): void {
    this.detailLoading.set(true);
    this.api.getSystemLocation(id).subscribe({
      next: (res) => {
        const type = this.locationTypeCatalog().find((t) => t.id === res.data.locationTypeId);
        const loc = { ...res.data, fields: type?.fields || res.data.fields || [] };
        this.detail.set(loc);
        this.populateEditForm(loc);
        this.detailLoading.set(false);
      },
      error: () => {
        this.detail.set(null);
        this.detailLoading.set(false);
      },
    });
  }

  selectLocation(id: number): void {
    this.selectedId.set(id);
    this.expandAncestors(id);
    this.formModalOpen.set(true);
    this.loadDetail(id);
  }

  closeFormModal(): void {
    this.formModalOpen.set(false);
  }

  isExpanded(id: number): boolean {
    return this.expandedIds().has(id);
  }

  toggleExpand(id: number, event?: Event): void {
    event?.stopPropagation();
    this.expandedIds.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  expandAll(): void {
    const ids = this.locations()
      .filter((l) => l.statusCd === 'active' && this.childCount(l.id) > 0)
      .map((l) => l.id);
    this.expandedIds.set(new Set(ids));
  }

  collapseAll(): void {
    this.expandedIds.set(new Set());
  }

  onTreeFilter(value: string): void {
    this.treeFilter.set(value);
    const q = value.trim().toLowerCase();
    if (!q) return;
    const ids = new Set<number>();
    const walk = (nodes: LocTreeNode[]) => {
      for (const node of nodes) {
        if (node.children.length) ids.add(node.location.id);
        walk(node.children);
      }
    };
    walk(this.visibleTree());
    this.expandedIds.set(ids);
  }

  childCount(locationId: number): number {
    return this.locations().filter((l) => l.parentLocationId === locationId && l.statusCd === 'active')
      .length;
  }

  cardMeta(loc: SystemLocation): string {
    const parts = [loc.locationTypeName];
    const inside = this.childCount(loc.id);
    if (inside) parts.push(`${inside} inside`);
    if (['AUTHORITY', 'ZONE', 'SECTOR', 'PLOT'].includes(loc.locationTypeCode)) {
      parts.push('LiDAR upload');
    }
    return parts.join(' · ');
  }

  typeInitial(name: string): string {
    return (name || '?').charAt(0).toUpperCase();
  }

  typeTone(name: string): string {
    const key = (name || '').toLowerCase();
    if (key.includes('authority')) return 'auth';
    if (key.includes('zone')) return 'zone';
    if (key.includes('sector')) return 'sector';
    if (key.includes('plot')) return 'plot';
    return 'default';
  }

  attrValue(loc: SystemLocation, key: string): string {
    const v = loc.attributes?.[key];
    if (v == null || v === '') return '—';
    return String(v);
  }

  formTitle(loc: SystemLocation): string {
    const titles: Record<string, string> = {
      AUTHORITY: 'Authority form',
      ZONE: 'Zone form',
      SECTOR: 'Sector form',
      PLOT: 'Plot form',
    };
    return titles[loc.locationTypeCode] ?? `${loc.locationTypeName} form`;
  }

  formLead(loc: SystemLocation): string {
    const leads: Record<string, string> = {
      AUTHORITY: 'Jurisdiction and leadership details — LiDAR scans can attach at authority level.',
      ZONE: 'Ward and zone officer information — LiDAR scans can attach at zone level.',
      SECTOR: 'Layout sector details — LiDAR scans attach here or at any parent/child level.',
      PLOT: 'Individual plot / khasra record — LiDAR can upload for this single plot.',
    };
    return leads[loc.locationTypeCode] ?? `Edit this ${loc.locationTypeName.toLowerCase()} record.`;
  }

  basicSectionTitle(loc: SystemLocation): string {
    const titles: Record<string, string> = {
      AUTHORITY: 'Authority identity',
      ZONE: 'Zone identity',
      SECTOR: 'Sector identity',
      PLOT: 'Plot identity',
    };
    return titles[loc.locationTypeCode] ?? 'Basic details';
  }

  attributesSectionTitle(loc: SystemLocation): string {
    const titles: Record<string, string> = {
      AUTHORITY: 'Legal & leadership',
      ZONE: 'Zone administration',
      SECTOR: 'Layout information',
      PLOT: 'Property details',
    };
    return titles[loc.locationTypeCode] ?? 'Additional details';
  }

  childTypeForParent(): string | null {
    const map: Record<string, string> = {
      AUTHORITY: 'ZONE',
      ZONE: 'SECTOR',
      SECTOR: 'PLOT',
    };
    return map[this.selectedTypeCode()] ?? null;
  }

  childTypeLabel(code: string): string {
    return this.locationTypeCatalog().find((t) => t.code === code)?.name ?? code;
  }

  parentLocationLabel(): string {
    const path = this.selectionPath();
    if (path.length < 2) return 'Top level';
    return path[path.length - 2].name;
  }

  lidarSurveyNote(): string {
    const loc = this.detail();
    return loc ? this.lidarSurveyNoteFor(loc) : '';
  }

  lidarSurveyNoteFor(loc: SystemLocation): string {
    const code = (loc.locationTypeCode || '').toUpperCase();
    if (code === 'PLOT') {
      return 'Upload drone and vehicle scans for this single plot.';
    }
    if (code === 'AUTHORITY') {
      return 'Upload scans for the whole authority — all descendant plots in scope are matched.';
    }
    if (code === 'ZONE') {
      return 'Upload scans for this zone — all plots under the zone are matched.';
    }
    return 'Upload drone and vehicle scans for this sector and its registered plots.';
  }

  openLidarSurvey(locationId: number): void {
    this.closeFormModal();
    this.lidarModalLocationId.set(locationId);
    this.lidarModalOpen.set(true);
  }

  closeLidarModal(): void {
    this.lidarModalOpen.set(false);
    this.lidarModalLocationId.set(null);
  }

  submitEditLocation(): void {
    const loc = this.detail();
    if (!loc || !this.canManageLocations()) return;

    if (!this.editForm.name.trim() || !this.editForm.code.trim()) {
      this.showToast('Name and code are required');
      return;
    }

    for (const f of loc.fields || []) {
      if (f.is_required && !String(this.editForm.attributes[f.key] ?? '').trim()) {
        this.showToast(`${f.label} is required`);
        return;
      }
    }

    this.saving.set(true);
    this.api
      .updateSystemLocation(loc.id, {
        name: this.editForm.name.trim(),
        code: this.editForm.code.trim(),
        attributes: this.stringsToAttrs(this.editForm.attributes, loc.fields || []),
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.showToast(`${loc.locationTypeName} updated`);
          this.reload();
        },
        error: (err) => {
          this.saving.set(false);
          this.showToast(err?.error?.message || 'Update failed');
        },
      });
  }

  openAddChild(childTypeCode: string): void {
    this.closeFormModal();
    const type = this.locationTypeCatalog().find((t) => t.code === childTypeCode);
    if (!type || this.selectedId() == null) return;
    this.addForm = {
      locationTypeId: type.id,
      parentLocationId: this.selectedId(),
      code: '',
      name: '',
      attributes: {},
    };
    this.syncAddAttributes();
    this.addTypePicked.set(true);
    this.addModalOpen.set(true);
  }

  resetEditForm(): void {
    const loc = this.detail();
    if (loc) this.populateEditForm(loc);
  }

  openAddLocation(): void {
    this.addForm = {
      locationTypeId: null,
      parentLocationId: this.selectedId(),
      code: '',
      name: '',
      attributes: {},
    };
    this.addTypePicked.set(false);
    this.clearAddFormErrors();
    this.addModalOpen.set(true);
  }

  closeAddModal(): void {
    this.addModalOpen.set(false);
    this.clearAddFormErrors();
  }

  pickAddType(typeId: number): void {
    this.addForm.locationTypeId = typeId;
    this.syncAddAttributes();
    this.addTypePicked.set(true);
    this.addFormErrors.update((e) => ({ ...e, form: undefined }));
  }

  syncAddAttributes(): void {
    const type = this.locationTypeCatalog().find((t) => t.id === this.addForm.locationTypeId);
    const attrs: Record<string, string> = {};
    for (const f of type?.fields || []) {
      attrs[f.key] = this.addForm.attributes[f.key] ?? '';
    }
    this.addForm.attributes = attrs;
  }

  submitAddLocation(): void {
    const type = this.selectedTypeForAdd();
    const errors: { form?: string; name?: string; code?: string; attributes?: Record<string, string> } =
      {};

    if (!type) {
      errors.form = 'Pick a location type first.';
      this.addFormErrors.set(errors);
      return;
    }
    if (!this.addForm.name.trim()) {
      errors.name = 'Name is required.';
    }
    if (!this.addForm.code.trim()) {
      errors.code = 'Code is required.';
    }

    const attrErrors: Record<string, string> = {};
    for (const f of type.fields) {
      if (f.is_required && !String(this.addForm.attributes[f.key] ?? '').trim()) {
        attrErrors[f.key] = `${f.label} is required.`;
      }
    }
    if (Object.keys(attrErrors).length) {
      errors.attributes = attrErrors;
    }

    if (errors.name || errors.code || errors.attributes || errors.form) {
      errors.form = errors.form || 'Fix the highlighted fields to continue.';
      this.addFormErrors.set(errors);
      return;
    }

    this.clearAddFormErrors();

    this.saving.set(true);
    this.api
      .createSystemLocation({
        locationTypeId: type.id,
        parentLocationId: this.addForm.parentLocationId,
        code: this.addForm.code.trim(),
        name: this.addForm.name.trim(),
        attributes: this.stringsToAttrs(this.addForm.attributes, type.fields),
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.addModalOpen.set(false);
          this.showToast(`${type.name} added`);
          if (this.addForm.parentLocationId != null) {
            this.selectedId.set(this.addForm.parentLocationId);
            this.expandAncestors(this.addForm.parentLocationId);
          }
          this.reload();
        },
        error: (err) => {
          this.saving.set(false);
          this.addFormErrors.set({
            form: err?.error?.message || 'Create failed. Try again.',
          });
        },
      });
  }

  private clearAddFormErrors(): void {
    this.addFormErrors.set({});
  }

  parentOptionsForAdd(): SystemLocation[] {
    return [...this.locations()]
      .filter((l) => l.statusCd === 'active')
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  typeOptionsForAdd() {
    return [...this.locationTypeCatalog()].sort((a, b) => a.name.localeCompare(b.name));
  }

  private stringsToAttrs(
    strings: Record<string, string>,
    fields: LocationTypeField[]
  ): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    for (const f of fields) {
      const raw = strings[f.key];
      if (raw == null || raw === '') continue;
      out[f.key] = f.field_type === 'number' ? Number(raw) : raw;
    }
    return out;
  }

  private populateEditForm(loc: SystemLocation): void {
    const attrs: Record<string, string> = {};
    for (const f of loc.fields || []) {
      const v = loc.attributes?.[f.key];
      attrs[f.key] = v == null || v === '' ? '' : String(v);
    }
    this.editForm = {
      name: loc.name,
      code: loc.code,
      attributes: attrs,
    };
  }

  showToast(msg: string): void {
    this.toast.set(msg);
    setTimeout(() => this.toast.set(''), 3200);
  }

  private buildTree(locations: SystemLocation[]): LocTreeNode[] {
    const active = locations.filter((l) => l.statusCd === 'active');
    const byParent = new Map<number | null, SystemLocation[]>();
    for (const loc of active) {
      const pid = loc.parentLocationId;
      if (!byParent.has(pid)) byParent.set(pid, []);
      byParent.get(pid)!.push(loc);
    }
    for (const [, arr] of byParent) {
      arr.sort((a, b) => a.name.localeCompare(b.name));
    }
    const build = (parentId: number | null): LocTreeNode[] =>
      (byParent.get(parentId) || []).map((location) => ({
        location,
        children: build(location.id),
      }));
    return build(null);
  }

  private filterTree(nodes: LocTreeNode[], q: string): LocTreeNode[] {
    const out: LocTreeNode[] = [];
    for (const node of nodes) {
      const filteredChildren = this.filterTree(node.children, q);
      const selfMatch =
        node.location.name.toLowerCase().includes(q) ||
        node.location.code.toLowerCase().includes(q) ||
        node.location.locationTypeName.toLowerCase().includes(q);
      if (selfMatch || filteredChildren.length) {
        out.push({
          location: node.location,
          children: selfMatch ? node.children : filteredChildren,
        });
      }
    }
    return out;
  }

  private initTreeExpansion(): void {
    this.expandedIds.set(new Set());
  }

  private expandAncestors(id: number): void {
    const byId = new Map(this.locations().map((l) => [l.id, l]));
    const ids = new Set(this.expandedIds());
    let cur = byId.get(id);
    while (cur?.parentLocationId != null) {
      ids.add(cur.parentLocationId);
      cur = byId.get(cur.parentLocationId);
    }
    this.expandedIds.set(ids);
  }
}
